import {FC, MouseEvent, useEffect, useState} from "react";
import {IconButton, Badge, Menu, MenuItem, Typography, ListItemText, Divider, Box} from "@mui/material";
import NotificationsIcon from "@mui/icons-material/Notifications";
import { REFRESH_INTERVAL_MS, STALE_TIME } from "../../../utils/constants";
import type { UserClass } from "../../../api-client/models";
import type { Notification } from "../../../api-client/models/notification";
import { myMutate, useUser } from "../../../hooks/useUser";
import RouterLink from "../../RouterLink/RouterLink";
import {
  useNotificationsList,
  notificationsBulkMarkAsReadCreate,
  getNotificationsListQueryKey,
} from "../../../api-client/notifications/notifications";

interface NotificationDisplay {
  id: number;
  text: string;
  link: string;
  isRead: boolean;
  createdAt: string;
}

const linkMap: Record<string, string> = {
  Trade: "/app/trade",
  NewTrade: "/app/trade",
  TradeAlliance: "/app/diplomacy?tab=trade_alliances",
  MilitaryAlliance: "/app/diplomacy?tab=military_alliances",
  NewTradeAlliance: "/app/diplomacy?tab=trade_alliances",
  NewMilitaryAlliance: "/app/diplomacy?tab=military_alliances",
  PeaceTreaty: "/app/diplomacy?tab=peace_treaties",
  War: "/app/diplomacy?tab=wars",
  Subpoena: "/app/diplomacy?tab=subpoenas",
  Warning: "/app/diplomacy?tab=warnings",
  Purchase: "/app/development?tab=pending",
  Takeover: "/app/map",
  BadgeAward: "/app/nations?nation=",
  UserClassBadgeAward: "/app/profile/0",
};

const getNotificationLink = (notification: Notification): string => {
  if (notification.modelName === "Purchase") {
    // @ts-ignore
    return {
      PURCHASE_PLACED: "/app/map",
      SENDER_ACCEPTED: "/app/development?tab=approved",
      SENDER_REJECTED: "/app/development?tab=declined",
    }[notification.notificationStatus] || "";
  }
  const base = linkMap[notification.modelName] || "";
  if (notification.modelName === "BadgeAward") {
    return base + (notification.offeringNation?.id ?? "");
  }
  return base;
};

const getNotificationText = (notification: Notification, user: UserClass): string => {
  if (notification.notificationText) return notification.notificationText;

  const receivingNation = notification.receivingNation?.name || "";
  const offeringNation = notification.offeringNation.name;
  const modelNameMap: Record<string, string> = {
    Trade: "Trade",
    TradeAlliance: "Trade Alliance",
    MilitaryAlliance: "Military Alliance",
    War: "War",
    Subpoena: "Subpoena",
    Warning: "Warning",
    Purchase: "Purchase",
    Attack: "Attack",
    Takeover: "Takeover",
  };

  const tradeType = modelNameMap[notification.modelName] || "";
  const direction = notification.offeringNation.id === user.nation.id ? "SENDING" : "RECEIVING";

  const notificationTemplates: Record<string, Record<string, Record<string, string>>> = {
    Purchase: {
      SENDING: {
        CREATED: "Your nation has proposed a project",
        SENDER_ACCEPTED: "Your nation approved a project",
        SENDER_REJECTED: "The purchase was declined",
      },
    },
    Attack: {
      SENDING: {
        CREATED: `Your nation has proposed an attack against ${receivingNation}`,
        SENDER_ACCEPTED: `Your nation performed an attack against ${receivingNation}`,
        SENDER_REJECTED: `The attack against ${receivingNation} was declined`,
      },
      RECEIVING: {
        SENDER_ACCEPTED: `Your nation has been attacked by ${offeringNation}`,
      },
    },
    Trade: {
      SENDING: {
        CREATED: `Your nation has proposed a ${tradeType} with ${receivingNation}`,
        SENDER_ACCEPTED: `Your nation has sent a ${tradeType} to ${receivingNation}`,
        SENDER_REJECTED: `Your nation has declined a ${tradeType} with ${receivingNation}`,
        SENDER_CANCELED: `Your nation has canceled a ${tradeType} with ${receivingNation}`,
      },
      RECEIVING: {
        SENDER_ACCEPTED: `Your nation has received a ${tradeType} from ${offeringNation}`,
        SENDER_CANCELED: `${offeringNation} has rescinded a ${tradeType} offer`,
        RECEIVER_ACCEPTED: `Your nation has accepted a ${tradeType} from ${offeringNation}`,
        RECEIVER_DECLINED: `Your nation has declined a ${tradeType} from ${offeringNation}`,
      },
    },
  };

  // Trade is used as a fallback for many trade types
  return notificationTemplates[tradeType]?.[direction]?.[notification.notificationStatus] || notificationTemplates["Trade"]?.[direction]?.[notification.notificationStatus] || "";
};

const NotificationsDropdown: FC = () => {
  const user = useUser();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [notifications, setNotifications] = useState<NotificationDisplay[]>([]);

  const { data: notificationsResponse } = useNotificationsList(
    undefined,
    {
      query: {
        refetchInterval: REFRESH_INTERVAL_MS || false,
        staleTime: STALE_TIME,
      },
    },
  );

  const notificationsData = notificationsResponse?.results;

  // notifications also gets an optimistic local update in handleClose below
  // (marks read ahead of the server), so it can't be a pure derivation of
  // notificationsData - it needs to stay effect-synced local state.
  useEffect(() => {
    if (notificationsData) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setNotifications(
          notificationsData.map(notification => ({
            id: notification.id,
            text: getNotificationText(notification, user),
            link: getNotificationLink(notification),
            isRead: notification.isRead,
            createdAt: new Date(notification.createdAt).toLocaleDateString(),
          })).filter(n => n.text)
      );
    }
  }, [notificationsData, user]);

  const handleClick = (event: MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
  const handleClose = () => {
    setAnchorEl(null);
    const unreadIds = notifications.filter(n => !n.isRead).map(n => n.id);
    if (unreadIds.length > 0) {
      notificationsBulkMarkAsReadCreate({ notificationIds: unreadIds })
        .then(() => myMutate(getNotificationsListQueryKey()[0]));
    }
    setNotifications(notifications.map(n => ({ ...n, isRead: true })));
  };

  return (
    <>
      <IconButton color="inherit" onClick={handleClick} aria-label="View notifications">
        <Badge badgeContent={notifications.filter(n => !n.isRead).length} color="error">
          <NotificationsIcon />
        </Badge>
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        slotProps={{
          paper: {
            sx: {
              minWidth: 280,
              maxWidth: { xs: "calc(100vw - 16px)", sm: 500 },
              width: { xs: "calc(100vw - 16px)", sm: "auto" },
              "& .MuiMenu-list": {
                maxHeight: "50vh",
                overflowY: "auto",
                pt: 0,
              },
            },
          },
        }}
      >
        <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid", borderColor: "divider" }}>
          <Typography variant="subtitle2" sx={{
            fontWeight: 700
          }}>Notifications</Typography>
        </Box>
        {notifications.length ? (
            notifications.map((n, i) => (
                <Box key={n.id}>
                  <MenuItem
                      sx={{
                        backgroundColor: n.isRead ? "transparent" : "primary.50",
                        textWrap: "auto",
                        py: 1.5,
                        alignItems: "flex-start",
                        ...(n.isRead ? {} : { bgcolor: "rgba(25, 118, 210, 0.06)" }),
                      }}
                      onClick={handleClose}
                      component={RouterLink}
                      to={`${n.link}${n.link.includes("?") ? "&" : "?"}notificationId=${n.id}`}
                  >
                    {!n.isRead && (
                      <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "primary.main", mt: 0.75, mr: 1.5, flexShrink: 0 }} />
                    )}
                    {n.isRead && <Box sx={{ width: 8, mr: 1.5, flexShrink: 0 }} />}
                    <ListItemText
                      primary={n.text}
                      secondary={n.createdAt}
                      slotProps={{
                        primary: { variant: "body2", sx: { fontWeight: n.isRead ? 400 : 600 } },
                        secondary: { variant: "caption" }
                      }} />
                  </MenuItem>
                  {i < notifications.length - 1 && <Divider />}
                </Box>
            ))
        ) : (
            <MenuItem onClick={handleClose} sx={{ py: 3, justifyContent: "center" }}>
              <Typography variant="body2" sx={{
                color: "text.secondary"
              }}>No notifications</Typography>
            </MenuItem>
        )}
      </Menu>
    </>
  );
};

export default NotificationsDropdown;
